import React, { useCallback, useEffect, useRef, useState, type ChangeEventHandler } from 'react';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { RegistrationFormData } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { checkConsentsFromValues, extractConsentValues, formatConsentsFromValues } from '@jwp/ott-common/src/utils/collection';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { PATH_HOME } from '@jwp/ott-common/src/paths';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { ACCESS_MODEL, COOKIE_DOMAIN, getLocalStorageValue, PENDING_LIBRARY_SELECTION_KEY, PENDING_LIBRARY_SIGNUP_KEY } from '@jwp/ott-common/src/constants';
import useOffers from '@jwp/ott-hooks-react/src/useOffers';
import Cookies from 'js-cookie';
import { getLibraryById, signupUser, type Restriction, type UserData } from '@jwp/ott-common/src/controllers/graphql';
import { useMutation } from 'react-query';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';

import { getSiteType } from '../../../utils/useSiteType';
import RegistrationForm from '../../../components/RegistrationForm/RegistrationForm';
import LibraryAutocomplete, { type LibrarySelectionPayload } from '../../../components/libraryautocomplete/Autocomplete';
import LibraryAccess from '../../../components/LibraryAccessForm/LibraryAccess';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';


const REGISTRATION_STEPS = ['library', 'library-access', 'registration'] as const;

type RegistrationStepId = (typeof REGISTRATION_STEPS)[number];

type LibraryByIdData = {
  libraryId: string;
  libraryName: string;
  libraryType: string;
  libraryBranch?: { libraryId: string; libraryName: string; libraryType: string }[];
  restrictionType?: { restriction: Restriction }[];
};

const normalizeLibraryName = (name?: string) => name?.trim().toLowerCase() ?? '';

const selectLibraryBranch = (
  branches: NonNullable<LibraryByIdData['libraryBranch']>,
  { libraryId, parentLibraryName }: { libraryId?: string; parentLibraryName?: string },
) => {
  if (!branches.length) return undefined;

  const branchById = libraryId ? branches.find((item) => item.libraryId === libraryId) : undefined;
  if (branchById) return branchById;

  const normalizedParentName = normalizeLibraryName(parentLibraryName);
  if (normalizedParentName) {
    const branchByName = branches.find((item) => normalizeLibraryName(item.libraryName) === normalizedParentName);
    if (branchByName) return branchByName;
  }

  return branches[0];
};

const toLibrarySelection = (libraryId: string, library: LibraryByIdData): LibrarySelectionPayload | null => {
  const libraryParent = library.restrictionType?.map((item) => item.restriction).filter(Boolean) ?? [];
  const branch = selectLibraryBranch(library.libraryBranch ?? [], {
    libraryId,
    parentLibraryName: library.libraryName,
  });

  if (!branch) return null;

  return {
    libraryId: branch.libraryId,
    libraryName: branch.libraryName,
    libraryParent,
    withInOrganization: true,
    libraryType: branch.libraryType,
  };
};

type Props = {
  onDialogSizeChange?: (size: 'small' | 'large') => void;
};

const Registration = ({ onDialogSizeChange }: Props) => {
  const accountController = getModule(AccountController);

  const navigate = useNavigate();
  const location = useLocation();
  const libraryIdParam = useQueryParam('libraryId');
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  const [consentValues, setConsentValues] = useState<Record<string, string | boolean>>({});
  const [consentErrors, setConsentErrors] = useState<string[]>([]);
  const [siteType, setSiteType] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<RegistrationStepId>('library');
  const [selectedLibrary, setSelectedLibrary] = useState<LibrarySelectionPayload | null>(null);
  const [isLibrarySignupLoading, setIsLibrarySignupLoading] = useState(false);
  const [isResolvingLibraryFromUrl, setIsResolvingLibraryFromUrl] = useState(false);
  const hasCompletedRegistrationRef = useRef(false);
  const hasAttemptedUrlLibraryResolve = useRef(false);
  const accessModel = useConfigStore((s) => s.accessModel);
  const defaultLanguage = useConfigStore((s) => s.config?.custom?.defaultLanguage as string | undefined);
  const localStorageLanguage = getLocalStorageValue() as string | undefined;
  const { mediaOffers } = useOffers();
  const hasMediaOffers = mediaOffers.length > 0;

  const { publisherConsents, publisherConsentsLoading, loading } = useAccountStore(({ publisherConsents, publisherConsentsLoading, loading }) => ({
    publisherConsents,
    publisherConsentsLoading,
    loading,
  }));

  const selectedLibraryData = JSON.parse(sessionStorage.getItem(PENDING_LIBRARY_SELECTION_KEY) || '{}') as LibrarySelectionPayload;

  const signupMutation = useMutation(signupUser, {
    onSuccess: (data) => {
      if (data) {
        Cookies.set('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }), {
          domain: COOKIE_DOMAIN,
          expires: 7,
        });
        localStorage.setItem('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }));
        sessionStorage.removeItem(PENDING_LIBRARY_SELECTION_KEY);
        sessionStorage.removeItem(PENDING_LIBRARY_SIGNUP_KEY);
        accountController.getAccount();
      }
    },
    onError: (error) => {
      console.error('Failed to sync selected library after registration:', error);
      if(siteType === 'public') {
        navigate(modalURLFromLocation(location, 'library-access'), {
          state: {
            libraryId: selectedLibraryData?.libraryId,
            libraryName: selectedLibraryData?.libraryName,
            libraryParent: selectedLibraryData?.libraryParent,
            withInOrganization: selectedLibraryData?.withInOrganization,
            libraryType: selectedLibraryData?.libraryType,
            signupErrorMessage: error instanceof Error ? error.message : 'Library access verification failed.',
          },
          replace: true,
        });
      }else {
        console.log('navigate to Library-auto-complete', error);
        navigate(modalURLFromLocation(location, 'Library-auto-complete'), {
          state: {
            signupErrorMessage: error instanceof Error ? error.message : 'Library access verification failed.',
          },
          replace: true,
        });
      }
    },
  });

  const handleChangeConsent: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ currentTarget }) => {
    if (!currentTarget) return;

    const { name, type } = currentTarget;
    const value = type === 'checkbox' ? (currentTarget as HTMLInputElement).checked : currentTarget.value;

    setConsentValues((current) => ({
      ...current,
      [name]: value,
    }));

    // Clear the errors for any checkbox that's toggled
    setConsentErrors((errors) => errors.filter((e) => e !== name));
  };

  useEffect(() => {
    if (!publisherConsents) {
      accountController.getPublisherConsents();

      return;
    }

    setConsentValues(extractConsentValues(publisherConsents));
  }, [accountController, publisherConsents]);

  useEffect(() => {
    const siteType = getSiteType();
    setSiteType(siteType);
  }, []);

  const handleLibrarySelected = useCallback((libraryData: LibrarySelectionPayload) => {
    setSelectedLibrary(libraryData);
    sessionStorage.setItem(PENDING_LIBRARY_SELECTION_KEY, JSON.stringify(libraryData));
    setCurrentStep(siteType === 'public' ? 'library-access' : 'registration');
  }, [siteType]);

  useEffect(() => {
    if (!siteType) return;

    const persistedSignupPayload = sessionStorage.getItem(PENDING_LIBRARY_SIGNUP_KEY);
    if (persistedSignupPayload) {
      setCurrentStep('registration');
      return;
    }

    const persistedSelection = sessionStorage.getItem(PENDING_LIBRARY_SELECTION_KEY);
    if (persistedSelection) {
      try {
        const parsedSelection = JSON.parse(persistedSelection) as LibrarySelectionPayload;
        setSelectedLibrary(parsedSelection);
        setCurrentStep(siteType === 'public' ? 'library-access' : 'registration');
      } catch {
        sessionStorage.removeItem(PENDING_LIBRARY_SELECTION_KEY);
        setCurrentStep('library');
      }
      return;
    }

    setCurrentStep('library');
  }, [siteType]);


  useEffect(() => {
    if (!siteType || !libraryIdParam || hasAttemptedUrlLibraryResolve.current) return;

    // If a login token already exists, the user is signed in — send them to the home screen.
    if (localStorage.getItem('inplayer_token')) {
      navigate(PATH_HOME, { replace: true });
    }

    if (sessionStorage.getItem(PENDING_LIBRARY_SIGNUP_KEY) || sessionStorage.getItem(PENDING_LIBRARY_SELECTION_KEY)) {
      return;
    }

    hasAttemptedUrlLibraryResolve.current = true;
    let cancelled = false;

    const resolveLibraryFromUrl = async () => {
      setIsResolvingLibraryFromUrl(true);

      try {
        const data = await getLibraryById(libraryIdParam);
        const library = data?.GetLibraryById as LibraryByIdData | null | undefined;
        if (cancelled || !library) return;

        const selection = toLibrarySelection(libraryIdParam, library);
        if (!selection) return;

        handleLibrarySelected(selection);
      } catch (error) {
        console.error('Failed to resolve library from URL:', error);
      } finally {
        if (!cancelled) {
          setIsResolvingLibraryFromUrl(false);
        }
      }
    };

    void resolveLibraryFromUrl();

    return () => {
      cancelled = true;
    };
  }, [siteType, libraryIdParam, handleLibrarySelected]);

  useEffect(() => {
    if (!onDialogSizeChange) return;

    const size = currentStep === 'registration' ? 'small' : 'large';
    onDialogSizeChange(size);
  }, [currentStep, onDialogSizeChange]);

  useEffect(() => () => {
    if (currentStep === 'registration' && !hasCompletedRegistrationRef.current) {
      sessionStorage.removeItem(PENDING_LIBRARY_SELECTION_KEY);
      sessionStorage.removeItem(PENDING_LIBRARY_SIGNUP_KEY);
    }
  }, [currentStep]);


  const handleLibraryAccessStepComplete = useCallback((payload: Omit<UserData, 'userToken'>) => {
    sessionStorage.setItem(PENDING_LIBRARY_SIGNUP_KEY, JSON.stringify(payload));
    setCurrentStep('registration');
  }, []);

  const nextStep = useCallback(() => {
    const hasOffers = accessModel === ACCESS_MODEL.SVOD || (accessModel === ACCESS_MODEL.AUTHVOD && hasMediaOffers);
    navigate(modalURLFromLocation(location, hasOffers ? 'choose-offer' : 'welcome'), {
      state: { isSignUp: true },
      replace: true,
    });
  }, [navigate, location, accessModel, hasMediaOffers]);

  const proceedAfterRegistration = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const syncSelectedLibraryAfterRegistration = useCallback(async (callbacks?: { onComplete?: () => void; onError?: (error: unknown) => void }) => {
    const persistedSignupPayload = sessionStorage.getItem(PENDING_LIBRARY_SIGNUP_KEY);
    const persistedSelection = sessionStorage.getItem(PENDING_LIBRARY_SELECTION_KEY);

    if (!persistedSignupPayload && !persistedSelection) {
      callbacks?.onComplete?.();
      return;
    }

    let userToken = '';
    try {
      const storedToken = localStorage.getItem('inplayer_token');
      if (storedToken) {
        userToken = JSON.parse(storedToken).token;
      }
    } catch (error) {
      console.error('Error parsing token from localStorage:', error);
      return;
    }

    if (!userToken) {
      return;
    }
    let payloadWithoutToken: Omit<UserData, 'userToken'> | null = null;

    if (persistedSignupPayload) {
      try {
        payloadWithoutToken = JSON.parse(persistedSignupPayload) as Omit<UserData, 'userToken'>;
      } catch {
        sessionStorage.removeItem(PENDING_LIBRARY_SIGNUP_KEY);
      }
    }

    if (!payloadWithoutToken && persistedSelection) {
      try {
        const parsedSelection = JSON.parse(persistedSelection) as LibrarySelectionPayload;
        payloadWithoutToken = {
          libraryId: parsedSelection.libraryId,
          isAdult: true,
          skipped: true,
          languageCode: localStorageLanguage ?? defaultLanguage,
        } as Omit<UserData, 'userToken'>;
      } catch {
        sessionStorage.removeItem(PENDING_LIBRARY_SELECTION_KEY);
      }
    }

    if (!payloadWithoutToken?.libraryId) {
      callbacks?.onComplete?.();
      return;
    }

    setIsLibrarySignupLoading(true);
    try {
      await signupMutation.mutateAsync({
        userToken,
        ...payloadWithoutToken,
      });
      callbacks?.onComplete?.();
    } catch (error) {
      console.error('First signup attempt failed:', error);
    } finally {
      setIsLibrarySignupLoading(false);
    }
  }, [defaultLanguage, localStorageLanguage, signupMutation]);

  const { handleSubmit, handleChange, handleBlur, values, errors, validationSchemaError, submitting } = useForm<RegistrationFormData>({
    initialValues: { email: '', password: '' },
    validationSchema: object().shape({
      email: string()
        .email(t('registration.field_is_not_valid_email'))
        .required(t('registration.field_required', { field: t('registration.email') })),
      password: string()
        .matches(/^(?=.*[a-z])(?=.*\d).{8,}$/, t('registration.invalid_password', { field: t('registration.password') }))
        .required(t('registration.field_required', { field: t('registration.password') })),
    }),
    validateOnBlur: true,
    onSubmit: async ({ email, password }) => {
      const { consentsErrors } = checkConsentsFromValues(publisherConsents || [], consentValues);

      if (consentsErrors.length) {
        setConsentErrors(consentsErrors);
        throw new Error(t('registration.consents_error'));
      }

      await accountController.register(email, password, globalThis.location.href, formatConsentsFromValues(publisherConsents, consentValues), siteType);
    },
    onSubmitSuccess: (result) => {
      if (result) {
        hasCompletedRegistrationRef.current = true;
        if (selectedLibrary) {
          sessionStorage.setItem(PENDING_LIBRARY_SELECTION_KEY, JSON.stringify(selectedLibrary));
        }
        localStorage.setItem('IsLibraryOnboarded', JSON.stringify({ isLibraryOnboarded: false }));
        sessionStorage.setItem('isSignInProgress', 'true');

        void syncSelectedLibraryAfterRegistration({
          onComplete: () => {
            announce(t('registration.success'), 'success');
            proceedAfterRegistration();
          },
        });
      }
    },
    onSubmitError: ({ resetValue }) => resetValue('password'),
  });

  const stepProgress = (current: number, total: number) => t('registration.step_progress', { current, total });

  let stepContent: React.ReactNode;
  if (currentStep === 'library') {
    stepContent = isResolvingLibraryFromUrl ? (
      <div style={{ height: 300 }}>
        <LoadingOverlay inline />
      </div>
    ) : (
      <LibraryAutocomplete onLibrarySelected={handleLibrarySelected} registrationStepLabel={stepProgress(2, 3)} />
    );
  } else if (currentStep === 'library-access') {
    stepContent = (
      <LibraryAccess
        onCloseButtonClick={() => setCurrentStep('library')}
        initialLibrarySelection={selectedLibrary}
        onSubmitForRegistrationStep={handleLibraryAccessStepComplete}
        onBackForRegistrationStep={() => setCurrentStep('library')}
      />
    );
  } else {
    stepContent = (
      <RegistrationForm
        onSubmit={handleSubmit}
        onChange={handleChange}
        onConsentChange={handleChangeConsent}
        onBlur={handleBlur}
        values={values}
        errors={errors}
        consentErrors={consentErrors}
        validationError={validationSchemaError}
        submitting={submitting || isLibrarySignupLoading}
        consentValues={consentValues}
        publisherConsents={publisherConsents}
        loading={loading || publisherConsentsLoading}
        stepProgressLabel={stepProgress(3, 3)}
      />
    );
  }

  return (
    <section>
      {stepContent}
    </section>
  );
};

export default Registration;
